import { Link } from "react-router-dom";
import { PolicyLayout, PolicySection } from "@/components/PolicyLayout";

const SUPPORT_FORM_URL =
  "https://forms.zohopublic.com/foodiecrave4ugm1/form/CustomerSupportForm/formperma/RzlQJWKo2FHdmJj54ES7g4Hpv0gkn1cX5RIONNP7omw";

const Sub = ({ children }) => (
  <h3 className="pt-2 font-display text-xl font-medium tracking-tight text-crave-cream">
    {children}
  </h3>
);

const EmailLink = () => (
  <a
    href="mailto:support@foodiecrave.app"
    className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
  >
    support@foodiecrave.app
  </a>
);

export default function Terms() {
  return (
    <PolicyLayout
      testId="terms-page"
      seoTitle="Terms of Service — Foodie Crave"
      seoDescription="The terms that govern your use of Foodie Crave, including accounts, Free and Premium memberships, billing, and acceptable use."
      seoPath="/terms"
      title="Terms of Service"
      updated="October 20, 2026"
    >
      <PolicySection title="1. Acceptance of Terms">
        <p>
          By downloading, accessing, or using Foodie Crave ("the app"), you agree to these Terms of
          Service. You must agree to the applicable Terms when creating an account or using
          account-required features. If you do not agree, please do not use the app.
        </p>
        <p>
          We may update these terms from time to time; material updates will be communicated as
          described in Section 13, and continued use after changes take effect means you accept
          them.
        </p>
      </PolicySection>

      <PolicySection title="2. The Service">
        <p>
          Foodie Crave is a restaurant discovery and dining-planning application designed to help
          users discover restaurants, cafés, food trucks, and other eligible food and beverage
          establishments.
        </p>
        <p>
          Depending on account status, membership level, and feature availability, users may save
          places, track visited locations, write reviews, connect with friends, organize
          in-person dining invitations, and vote on where to eat.
        </p>
        <p>
          Certain features may be introduced, modified, limited, or discontinued during Early Access
          as the application continues to develop.
        </p>
        <p>
          Restaurant information, including operating hours, menus, availability, and other business
          details, may be provided by third-party services and may change without notice.
        </p>
        <p>
          Foodie Crave does not own, operate, or independently endorse the establishments displayed
          in the application.
        </p>
      </PolicySection>

      <PolicySection title="3. Accounts and Eligibility">
        <p>
          You must be at least <span className="font-semibold text-crave-cream">13 years old</span>{" "}
          (or 16 in the European Economic Area) to use Foodie Crave. To purchase a subscription, you
          must be at least <span className="font-semibold text-crave-cream">18 years old</span> or
          the age of majority in your jurisdiction.
        </p>
        <p>
          You are responsible for keeping your account credentials confidential and for all activity
          under your account. Provide accurate information when you sign up and keep it up to date.
        </p>
        <p>
          You are responsible for maintaining accurate account information and using Foodie Crave in
          a lawful and respectful manner. Account access may be restricted where necessary to
          protect other users, maintain service security, or comply with applicable law.
        </p>
      </PolicySection>

      <PolicySection title="4. Plans, Billing, and Trials">
        <Sub>Free Membership</Sub>
        <p>
          Foodie Crave offers a limited Free membership that provides access to selected application
          features. Feature allowances and restrictions are displayed within the application and may
          be adjusted as the service develops, subject to applicable notice and consumer protection
          requirements.
        </p>
        <Sub>Premium Membership</Sub>
        <p>
          Premium membership provides expanded access to supported Foodie Crave features, including
          increased allowances for Saved Places, Friends, Invitations, and other eligible
          functionality.
        </p>
        <p>
          The standard Premium subscription price is{" "}
          <span className="font-semibold text-crave-cream">$2.99 per month</span> or{" "}
          <span className="font-semibold text-crave-cream">$19.99 per year</span>. Eligible
          first-time Premium subscribers who choose annual billing may receive an introductory
          first-year price of <span className="font-semibold text-crave-cream">$15.99</span>. After
          the introductory period, the subscription renews at the standard annual rate unless a
          different renewal price is disclosed and agreed to during enrollment.
        </p>
        <Sub>Free Trial</Sub>
        <p>
          Eligible new Premium subscribers may receive a{" "}
          <span className="font-semibold text-crave-cream">30-day free trial</span>. Unless canceled
          before the trial expires, the subscription automatically converts to a paid membership at
          the price disclosed during enrollment. The applicable trial duration, initial charge,
          renewal price, and renewal frequency are clearly disclosed before purchase.
        </p>
        <Sub>Early Access and Promotional Memberships</Sub>
        <p>
          Foodie Crave may offer complimentary or discounted Premium access to eligible Early Access
          participants, testers, or promotional recipients. Promotional offers may include specific
          eligibility requirements, membership durations, and usage limitations. Any applicable
          restrictions will be disclosed when the offer is made.
        </p>
        <Sub>Billing and Cancellation</Sub>
        <p>
          Subscriptions purchased through Google Play or the Apple App Store are managed through the
          applicable platform. You may manage or cancel subscriptions through your respective app
          store account settings.
        </p>
        <p>
          Cancellation generally prevents future renewals but does not automatically provide a
          refund for an existing billing period. Refund requests are subject to applicable platform
          policies and consumer protection laws.
        </p>
        <p>Deleting a Foodie Crave account does not automatically cancel an active subscription.</p>
      </PolicySection>

      <PolicySection title="5. Acceptable Use">
        <p>You agree not to:</p>
        <ol className="list-decimal space-y-2 pl-5">
          <li>Use Foodie Crave for unlawful, fraudulent, or deceptive activities.</li>
          <li>Harass, intimidate, stalk, threaten, abuse, or impersonate another person.</li>
          <li>Repeatedly send unwanted invitations, friend requests, or other communications.</li>
          <li>
            Attempt to bypass another user's privacy settings, account restrictions, or blocking
            preferences.
          </li>
          <li>Submit fake, fraudulent, misleading, defamatory, or intentionally false reviews.</li>
          <li>
            Upload content that infringes another person's intellectual property or other legal
            rights.
          </li>
          <li>
            Access, collect, disclose, or misuse another user's personal information without
            authorization.
          </li>
          <li>
            Use automated tools to scrape restaurant listings, account information, or other
            application data without authorization.
          </li>
          <li>
            Reverse-engineer, disrupt, interfere with, or compromise Foodie Crave's systems,
            security, or normal operation, except where restrictions are prohibited by applicable
            law.
          </li>
          <li>
            Use Foodie Crave to facilitate illegal activities or create a risk to the safety of
            others.
          </li>
        </ol>
        <p>
          Foodie Crave may investigate reported violations and take appropriate action, including
          content removal, feature restrictions, account suspension, or termination. Enforcement
          actions may be taken when reasonably necessary to protect users, maintain service
          integrity, or comply with applicable law.
        </p>
      </PolicySection>

      <PolicySection title="6. Intellectual Property">
        <p>
          The Foodie Crave name, logo, branding, application design, and proprietary content are
          owned by Foodie Crave or its applicable licensors.
        </p>
        <p>
          You retain ownership of the original content you create and submit through the
          application, subject to any applicable third-party rights. By submitting content, you
          grant Foodie Crave a non-exclusive, worldwide, royalty-free license to host, store,
          reproduce, and display that content as reasonably necessary to operate, maintain, and
          provide the service. This license does not transfer ownership of your content to Foodie
          Crave.
        </p>
        <p>
          When your content is deleted, the applicable license ends to the extent that the content
          is no longer retained or processed under legitimate legal, operational, or technical
          exceptions described in the Privacy Policy and{" "}
          <Link
            to="/data-deletion"
            className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
          >
            Data Retention &amp; Account Deletion Policy
          </Link>
          .
        </p>
      </PolicySection>

      <PolicySection title="7. Third-Party Services">
        <p>
          Foodie Crave uses or may integrate with third-party service providers to support
          restaurant discovery, mapping, account management, hosting, communications,
          notifications, and subscription processing.
        </p>
        <p>
          These services may include Google Maps Platform, Google Play, Apple App Store, and other
          providers as applicable to the functionality available. Certain third-party integrations
          may be introduced during or after Early Access.
        </p>
        <p>
          Use of third-party services may be subject to their respective terms and privacy policies.
          Foodie Crave does not control independently operated third-party websites, restaurants, or
          external services.
        </p>
        <p>
          Additional information about applicable service providers is available in our{" "}
          <Link
            to="/third-party"
            className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
          >
            Third-Party Services Disclosure
          </Link>{" "}
          and{" "}
          <Link
            to="/privacy"
            className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
          >
            Privacy Policy
          </Link>
          .
        </p>
      </PolicySection>

      <PolicySection title="8. Disclaimers">
        <p>
          The app is provided <span className="font-semibold text-crave-cream">"as is"</span> and{" "}
          <span className="font-semibold text-crave-cream">"as available"</span>, without warranties
          of any kind, express or implied, including merchantability, fitness for a particular
          purpose, and non-infringement.
        </p>
        <p>
          We do not guarantee that restaurant information — including menus, operating hours, or
          availability — is accurate, or that the app will be uninterrupted or error-free. Foodie
          Crave provides discovery and planning tools and does not guarantee the quality, safety,
          availability, or services of independent establishments. Your dining experience is between
          you and the venue.
        </p>
        <p>
          These disclaimers apply to the maximum extent permitted by applicable law and do not
          affect any consumer rights that cannot legally be waived.
        </p>
      </PolicySection>

      <PolicySection title="9. Indemnification">
        <p>
          To the extent permitted by applicable law, you agree to indemnify and hold harmless Foodie
          Crave and its team from claims, damages, liabilities, or expenses arising out of your
          misconduct or unlawful activity, your infringement of another person's rights, or your
          material violation of these Terms.
        </p>
      </PolicySection>

      <PolicySection title="10. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, Foodie Crave and its team will not be liable for
          any indirect, incidental, special, consequential, or punitive damages arising from your
          use of the app. Our total aggregate liability for any claim will not exceed the greater of
          (a) <span className="font-semibold text-crave-cream">US$100</span> or (b) the amount you
          paid us in the twelve months before the claim.
        </p>
        <p>
          These limitations apply only to the maximum extent permitted by applicable law and do not
          exclude liabilities that cannot legally be limited or excluded.
        </p>
      </PolicySection>

      <PolicySection title="11. Dispute Resolution and Governing Law">
        <p>
          <span className="font-semibold text-crave-cream">Informal resolution first.</span> Please
          email <EmailLink /> with any dispute. We will try to resolve it within 60 days.
        </p>
        <p>
          <span className="font-semibold text-crave-cream">Binding arbitration.</span> If informal
          resolution fails, disputes will be resolved by binding arbitration under the rules of the
          American Arbitration Association (AAA), seated in{" "}
          <span className="font-semibold text-crave-cream">Oregon</span>, subject to applicable law.
        </p>
        <p>
          <span className="font-semibold text-crave-cream">Class action waiver.</span> You waive the
          right to participate in a class action; claims must be brought on an individual basis.
        </p>
        <p>
          <span className="font-semibold text-crave-cream">Opt-out.</span> You may opt out of this
          arbitration clause by emailing us within 30 days of first accepting these Terms.
        </p>
        <p>
          <span className="font-semibold text-crave-cream">Governing law.</span> These Terms are
          governed by the laws of <span className="font-semibold text-crave-cream">Oregon</span>,
          without regard to its conflict-of-laws rules.
        </p>
      </PolicySection>

      <PolicySection title="12. Termination and Account Deletion">
        <p>
          You may request deletion of your Foodie Crave account through the application's account
          settings or other supported deletion request methods. Where permitted by applicable law,
          account deletion requests may include a seven-day recovery period during which you can
          cancel your deletion request. After the applicable recovery period, account deletion will
          proceed according to the{" "}
          <Link
            to="/data-deletion"
            className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
          >
            Data Retention &amp; Account Deletion Policy
          </Link>
          .
        </p>
        <p>
          Foodie Crave may suspend, restrict, or terminate accounts that violate these Terms,
          applicable laws, or the safety and security of the service. Where reasonably appropriate,
          you may receive notice of account restrictions or termination. We may take immediate
          action when necessary to address security threats, serious misconduct, or legal
          obligations.
        </p>
        <p>
          Certain provisions of these Terms may survive account termination where legally
          applicable.
        </p>
      </PolicySection>

      <PolicySection title="13. Changes to These Terms">
        <p>
          We may revise these terms occasionally. For material changes, we will provide notice at
          least 7 days before the new terms take effect, communicated through the application or by
          email. Where applicable law requires additional notice or affirmative acceptance, we will
          follow those requirements. The "Last updated" date above shows the latest revision.
        </p>
      </PolicySection>

      <PolicySection title="14. Contact Us">
        <p>
          Questions about these terms? Email us at <EmailLink /> or visit{" "}
          <a
            href="https://foodiecrave.app"
            className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
          >
            foodiecrave.app
          </a>
          . You may also reach us through our{" "}
          <a
            href={SUPPORT_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
          >
            Customer Support form
          </a>
          .
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
